import { isBedrockOpenAIModelRef } from "./bedrockOpenAi";
import {
  OPENAI_GPT_5_6_SOL_MODEL_ID,
  OPENAI_GPT_6_ASTRA_MODEL_ID,
  OPENAI_GPT_6_SOL_MODEL_ID,
} from "./openAi";

const ANTHROPIC_EPHEMERAL_PROMPT_CACHE_TTL_MS = 5 * 60 * 1000;
const ANTHROPIC_EXTENDED_PROMPT_CACHE_TTL_MS = 60 * 60 * 1000;
const OPENAI_IN_MEMORY_PROMPT_CACHE_TTL_MS = 55 * 60 * 1000;
const OPENAI_EXTENDED_PROMPT_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const OPENAI_GPT_5_6_PROMPT_CACHE_MINIMUM_TTL_MS = 30 * 60 * 1000;

// Providers whose wire request actually carries the cache_control TTL hint
// (see getCacheAnnotation in inMemoryModeRunner). Only for these does the
// resolved systemCacheTtl reflect the real retention window; elsewhere the hint
// never reaches the wire, so retention stays at the provider's own default.
const TTL_HINT_HONORING_PROVIDERS = new Set([
  "bedrock",
  "anthropic",
  "bedrock-anthropic",
]);

/**
 * Effective prompt-cache retention window for a model, used to classify
 * cross-wake cache breaks (sleptMs > retention ⇒ ttl_expired).
 *
 * For providers that honor the cache_control TTL hint (bedrock / anthropic /
 * bedrock-anthropic) the retention is whatever TTL the request actually carries
 * — the per-actor `systemCacheTtl` ("5m" | "1h"), defaulting to the actor
 * runtime's 1h system-cache policy when unspecified. Passing the resolved value keeps the break classifier
 * honest: a 1h-cache actor woken at 30m must not be reported as ttl_expired.
 * OpenAI-family retention is provider-fixed, and other providers (vertex,
 * google, openrouter, azure, …) never receive the hint, so systemCacheTtl is
 * ignored for all of them — they keep the 5m ephemeral default.
 */
export function getPromptCacheRetentionTtlMs(
  modelRef: string | null,
  systemCacheTtl?: "5m" | "1h",
): number {
  const normalizedModelId = normalizeModelId(modelRef);
  if (
    normalizedModelId === OPENAI_GPT_5_6_SOL_MODEL_ID ||
    normalizedModelId === OPENAI_GPT_6_ASTRA_MODEL_ID ||
    normalizedModelId === OPENAI_GPT_6_SOL_MODEL_ID
  ) {
    return OPENAI_GPT_5_6_PROMPT_CACHE_MINIMUM_TTL_MS;
  }
  if (isBedrockOpenAIModelRef(modelRef)) {
    return OPENAI_EXTENDED_PROMPT_CACHE_TTL_MS;
  }
  if (
    normalizedModelId === "gpt-5.5" ||
    normalizedModelId?.startsWith("gpt-5.5-")
  ) {
    return OPENAI_EXTENDED_PROMPT_CACHE_TTL_MS;
  }
  if (modelRef?.startsWith("openai/")) {
    return OPENAI_IN_MEMORY_PROMPT_CACHE_TTL_MS;
  }
  const resolvedSystemCacheTtl = systemCacheTtl ?? "1h";
  if (honorsTtlHint(modelRef) && resolvedSystemCacheTtl === "1h") {
    return ANTHROPIC_EXTENDED_PROMPT_CACHE_TTL_MS;
  }
  return ANTHROPIC_EPHEMERAL_PROMPT_CACHE_TTL_MS;
}

// Mirrors parseProvider (inMemoryModeRunner): the prefix before "/", with a
// bare ref (no slash) defaulting to "bedrock".
function honorsTtlHint(modelRef: string | null): boolean {
  if (modelRef === null) {
    return false;
  }
  const slashIndex = modelRef.indexOf("/");
  const provider =
    slashIndex === -1 ? "bedrock" : modelRef.slice(0, slashIndex);
  return TTL_HINT_HONORING_PROVIDERS.has(provider);
}

function normalizeModelId(modelRef: string | null): string | null {
  if (modelRef === null) {
    return null;
  }
  const slashIndex = modelRef.indexOf("/");
  const rawModelId =
    slashIndex === -1 ? modelRef : modelRef.slice(slashIndex + 1);
  return rawModelId
    .trim()
    .toLowerCase()
    .replace(/^(global\.|us\.|eu\.|apac\.)/, "")
    .replace(/^openai\./, "");
}

export const promptCacheRetentionTtl = {
  anthropicEphemeralMs: ANTHROPIC_EPHEMERAL_PROMPT_CACHE_TTL_MS,
  anthropicExtendedMs: ANTHROPIC_EXTENDED_PROMPT_CACHE_TTL_MS,
  openaiExtendedMs: OPENAI_EXTENDED_PROMPT_CACHE_TTL_MS,
  openaiGpt56MinimumMs: OPENAI_GPT_5_6_PROMPT_CACHE_MINIMUM_TTL_MS,
  openaiInMemoryMs: OPENAI_IN_MEMORY_PROMPT_CACHE_TTL_MS,
} as const;
